/* Moving a square that has already happened, and what it disturbs.
 *
 * The board has always been able to hand a *running* service to somebody else:
 * a client changes chairs, the square goes with them. What it could not do was
 * touch a square that had finished — and a finished square is precisely the one
 * that turns out to be wrong. The desk notices at four o'clock that the two
 * o'clock pedicure went up against Bea when Ana did it, and by then there is
 * nothing to drag: the service is over.
 *
 * That is the whole of this file. A finished square is a *record*, and three
 * things follow from the word:
 *
 *   it takes no chair    a record can be filed against somebody who is in
 *                        service this second, because it is not asking them to
 *                        do anything. Only a service still running needs its
 *                        new technician's hands free.
 *   it has a place       history is ordered. A square that happened at two
 *                        o'clock belongs above the one that happened at three,
 *                        so a move is "in front of that square", not "on the
 *                        end of that column" — and everything below it moves
 *                        down, because the rows were only ever a reading of
 *                        the order (see the sortAt note in store.js).
 *   it is never silent   moving one rewrites what the day says happened, which
 *                        is the one edit on this board nobody can see being
 *                        made. Every landing goes through a sheet, and the
 *                        sheet says what it will run over.
 *
 * The functions here are the arithmetic of that last part: what the column
 * would read like afterwards, and which of the things it would run over are
 * worth stopping to say out loud. They are pure — plain arrays of plain turns,
 * no reactivity, no clock beyond the one passed in — for the reason given at
 * the top of js/rules/turns.js: the answers decide who gets credited for
 * somebody else's work, and an answer like that needs tests.
 *
 * A turn, here, is `{ id, startedAt, endedAt }` and nothing else. Slots are
 * whatever buildSlots() made — see turns.js.
 */

/* ---- where the square lands ----
 *
 * A landing is an ANCHOR: the turn the moved one goes in front of, or null for
 * the end of the column. Rows are not the unit, because a row is a reading of
 * the order and the order is what is actually being changed — two half turns
 * sharing a square are one row and two positions, and a row number cannot tell
 * you which of them was meant.
 *
 * The moved turn is pulled out first whether or not it was in this column, so
 * the same call answers a move between technicians and a reorder within one.
 */
export function reorderTurns(list, turn, anchorId) {
  /* Dropped on itself. "In front of that square" is where it already is, so
   * this is the one landing that means nothing — and it has to be said here
   * rather than left to fall out: the removal below takes the square out of the
   * list, which takes the anchor with it, and a missing anchor is appended to
   * the end. A drop that aimed at nothing would quietly move the square to the
   * bottom of the column, which is the opposite of nothing. */
  if (anchorId != null && anchorId === turn.id) return (list || []).slice();
  var out = (list || []).filter(function (t) { return t.id !== turn.id; });
  if (anchorId == null) { out.push(turn); return out; }
  var i = out.findIndex(function (t) { return t.id === anchorId; });
  if (i === -1) { out.push(turn); return out; }
  out.splice(i, 0, turn);
  return out;
}

// Whether a drop actually changes anything. Dragging a square onto its own
// position, or onto the one directly below it, reads as a move and is not one
// — and a confirmation sheet for a move that does nothing is worse than no
// sheet at all, because saying yes to it teaches the desk that the sheet is
// noise.
export function sameOrder(a, b) {
  if (a.length !== b.length) return false;
  for (var i = 0; i < a.length; i++) if (a[i].id !== b[i].id) return false;
  return true;
}

/* ---- work the destination was already doing ----
 *
 * The conflict a moved record makes, and the only one that is a claim about
 * the world rather than about the board: this says Ana did a pedicure from two
 * until three, and Ana's column already says she did a manicure from half past
 * two until half past three. One of those is wrong, and the board cannot know
 * which — so it says so and lets the desk decide.
 *
 * A square still running is measured to `now`, which is as far as anybody can
 * say it has got.
 *
 * `minMs` is slack. Squares are started and stopped by hand, so two that touch
 * at the edges routinely overlap by seconds; flagging that would put a warning
 * on almost every move, which is how a warning stops being read.
 */
function spanOf(turn, now) {
  var end = turn.endedAt == null ? now : turn.endedAt;
  return [turn.startedAt, Math.max(turn.startedAt, end)];
}

export function overlapsWith(turn, others, now, minMs) {
  var floor = minMs || 0;
  var a = spanOf(turn, now);
  var out = [];
  (others || []).forEach(function (o) {
    if (!o || o.id === turn.id) return;
    var b = spanOf(o, now);
    var ms = Math.min(a[1], b[1]) - Math.max(a[0], b[0]);
    if (ms > floor) out.push({ turn: o, ms: ms });
  });
  return out.sort(function (x, y) { return y.ms - x.ms; });
}

/* ---- squares that re-form ----
 *
 * Half turns pair with whatever follows them, so putting a square into the
 * middle of a column can change what the squares *below* it are worth without
 * touching them: a half that used to pair with the manicure under it now pairs
 * with the inserted one, and the manicure becomes a square of its own. Nobody
 * dragging a pedicure onto turn three is thinking about turn four, which is
 * exactly why it has to be said.
 *
 * Who each turn shares its square with, as a key that can be compared.
 */
export function squareMates(slots) {
  var out = {};
  (slots || []).forEach(function (slot) {
    var ids = slot.turns.map(function (t) { return t.id; });
    slot.turns.forEach(function (t) {
      out[t.id] = ids.filter(function (x) { return x !== t.id; }).sort().join(',');
    });
  });
  return out;
}

// The turns whose square changes under them. The moved one is left out: its
// square changing is the point of the move, and putting the obvious at the top
// of a list of surprises is how the surprises get skipped.
export function pairingChanges(before, after, movedId) {
  var was = squareMates(before);
  var now = squareMates(after);
  var out = [];
  Object.keys(was).forEach(function (id) {
    if (id === movedId) return;
    if (now[id] == null) return;              // gone from this column entirely
    if (now[id] !== was[id]) out.push(id);
  });
  return out;
}

/* Which square a turn ends up in, counting the slashed missed squares above it
 * and any red skips woven through — the same number the gutter draws, worked
 * out over a column that does not exist yet. store.turnRowNumber answers this
 * for the board as it stands; the sheet has to answer it for the board as it
 * would stand, which is not a question the live one can be asked.
 */
export function squareNumberOf(rows, turnId, missed) {
  for (var i = 0; i < rows.length; i++) {
    if (rows[i].skip) continue;
    var hit = rows[i].turns.some(function (t) { return t.id === turnId; });
    if (hit) return (missed || 0) + i + 1;
  }
  return 0;
}

// Every turn's square number, for comparing one reading of a column with
// another.
function numbersOf(rows, missed) {
  var out = {};
  (rows || []).forEach(function (entry, i) {
    if (entry.skip) return;
    entry.turns.forEach(function (t) { out[t.id] = (missed || 0) + i + 1; });
  });
  return out;
}

/* How much of the column actually has to move out of the way.
 *
 * NOT "everything below the landing". A square dragged UP pushes down only the
 * squares it went past, and leaves everything below where it came from exactly
 * where it was — the column closes up behind it by the same amount it opened in
 * front. A sheet that promised the whole tail was moving would be describing a
 * different edit from the one about to happen.
 *
 * Counted in squares rather than turns: two half turns sharing one are one
 * thing that moves, not two. The moved square is left out — it is the thing
 * doing the moving.
 */
export function shiftedSquares(before, after, missed, movedId) {
  var was = numbersOf(before, missed);
  var now = numbersOf(after, missed);
  var seen = {};
  var n = 0;
  Object.keys(now).forEach(function (id) {
    if (id === movedId) return;
    if (was[id] == null || was[id] === now[id]) return;
    if (seen[now[id]]) return;                 // the other half of a square
    seen[now[id]] = true;
    n += 1;
  });
  return n;
}
